import { useState, useEffect, useRef } from 'react';
import api from '@/api';
import CodeGraphVisualization from './CodeGraphVisualization';
import {
  Loader2, Zap, Network, Database,
  GitPullRequest, GitMerge, Layout, Activity,
  Server, FileText, CheckCircle, RefreshCw,
  Search, Filter, ArrowUpRight, MessageSquare,
  BarChart3, FileCode
} from 'lucide-react';

// --- Components ---

function StatCard({ label, value, icon: Icon, color, subValue }) {
  return (
    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-start justify-between min-w-[200px]">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && <span className="text-xs text-gray-400">{subValue}</span>}
        </div>
      </div>
      <div className={`p-2 rounded-lg ${color} bg-opacity-10`}>
        <Icon className={`w-5 h-5 ${color.replace('bg-', 'text-')}`} />
      </div>
    </div>
  );
}

function RepositoryList({ repos, activeRepo, onSelect }) {
  if (!repos.length) return <div className="p-4 text-sm text-gray-500 text-center">No repositories indexed.</div>;

  return (
    <div className="space-y-2">
      {repos.map((repo) => (
        <button
          key={repo.repo_key}
          onClick={() => onSelect(repo.repo_key)}
          className={`w-full text-left p-3 rounded-lg border transition-all flex items-center justify-between group ${activeRepo === repo.repo_key
            ? 'bg-purple-50 border-purple-500 shadow-sm'
            : 'bg-white border-gray-200 hover:border-purple-200 hover:bg-gray-50'
            }`}
        >
          <div>
            <div className={`font-medium text-sm ${activeRepo === repo.repo_key ? 'text-purple-900' : 'text-gray-900'}`}>
              {repo.repo_key.split('/').pop()}
            </div>
            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[180px]">
              {repo.repo_key}
            </div>
          </div>
          {activeRepo === repo.repo_key && <CheckCircle className="w-4 h-4 text-purple-600" />}
        </button>
      ))}
    </div>
  );
}

function ActivityList({ items, onItemSelect, selectedItem }) {
  if (!items.length) return <div className="p-8 text-center text-gray-500">No activity found.</div>;

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={item.id || idx}
          onClick={() => onItemSelect(item)}
          className={`p-4 rounded-xl border cursor-pointer transition-all ${selectedItem?.id === item.id
            ? 'bg-blue-50 border-blue-500 shadow-sm'
            : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow-sm'
            }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${item.type === 'PullRequest' ? 'bg-green-100 text-green-700' :
                  item.type === 'Issue' ? 'bg-orange-100 text-orange-700' :
                    item.type === 'WorkflowRun' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                  }`}>
                  {item.type || 'Event'}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : 'Just now'}
                </span>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 truncate" title={item.name || item.title}>
                {item.name || item.title || item.id}
              </h4>
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {item.content || item.description || "No description provided."}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Data Graph Visualization Component - Server-rendered iframe version
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function DataGraphVisualization({ ragType = 'all' }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [layout, setLayout] = useState('spring');
  const iframeRef = useRef(null);

  const getIframeSrc = () => {
    const ragTypes = ragType === 'all' ? 'text,logs,metrics' : ragType;
    const params = new URLSearchParams({
      rag_types: ragTypes,
      layout: layout,
      max_nodes: '300'
    });
    return `${API_BASE}/api/data-rag/visualization/render?${params.toString()}`;
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
  }, [ragType, layout]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load graph visualization');
  };

  const handleRefresh = () => {
    setLoading(true);
    if (iframeRef.current) {
      iframeRef.current.src = getIframeSrc();
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ minHeight: '600px' }}>
      {/* Controls */}
      <div className="p-3 bg-white border-b flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            Data Knowledge Graph
          </span>
          <span className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">
            {ragType === 'all' ? 'All Types' : ragType}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={layout}
            onChange={(e) => setLayout(e.target.value)}
            className="px-2 py-1 border rounded text-xs bg-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="spring">Spring</option>
            <option value="kamada_kawai">Kamada-Kawai</option>
            <option value="circular">Circular</option>
          </select>
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Graph Container - Fills available space */}
      <div className="flex-1 relative min-h-0" style={{ minHeight: '550px' }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/80 z-10">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="ml-2 text-sm text-gray-600">Rendering...</span>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <p className="text-red-500 text-sm mb-2">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-3 py-1.5 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <iframe
          ref={iframeRef}
          src={getIframeSrc()}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          className="w-full h-full border-0"
          title="Data Graph Visualization"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
}

// --- Main Panel ---

export default function KnowledgeGraphPanel() {
  const [activeTab, setActiveTab] = useState('codebase');
  const [stats, setStats] = useState({ repos: 0, vectors: 0, documents: 0 });
  const [repos, setRepos] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [activityItems, setActivityItems] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRagType, setSelectedRagType] = useState('all');
  const [indexing, setIndexing] = useState(false);
  const [indexError, setIndexError] = useState(null);

  // Initial Fetch
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);

    // 1. Fetch Stats
    try {
      const statsRes = await api.get('/integrations/index/status');
      const sources = statsRes.data.sources || [];

      const codeStats = sources.filter(s => s.category === 'code');
      const knowledgeStats = sources.filter(s => s.category !== 'code');

      const totalVectors = sources.reduce((acc, s) => acc + (s.stats?.vectors || 0), 0);
      let totalDocs = knowledgeStats.reduce((acc, s) => acc + (s.stats?.documents || 0), 0);

      // If index is not yet built, fall back to "items synced" count for better UX
      if (totalDocs === 0) {
        totalDocs = sources.reduce((acc, s) => acc + (s.items_synced || 0), 0);
      }

      setStats(prev => ({
        ...prev,
        vectors: totalVectors,
        documents: totalDocs
      }));
    } catch (e) {
      console.error("Stats fetch failed:", e);
    }

    // 2. Fetch Repos
    try {
      const repoRes = await api.get('/code-graph/repos');
      const repoList = repoRes.data.repositories || [];
      setRepos(repoList);

      setStats(prev => ({
        ...prev,
        repos: repoList.length
      }));

      if (repoList.length > 0 && !selectedRepo) {
        setSelectedRepo(repoList[0].repo_key);
      }
    } catch (e) {
      console.error("Repo fetch failed:", e);
    }

    // 3. Fetch Activity (Independent of Index Status)
    try {
      // To match Data Sync, we need to fetch multiple sources and aggregate
      const activityRes = await api.get('/github/items?limit=100');
      // Also fetch workflow runs if available
      let workflowRuns = [];
      try {
        const wfRes = await api.get('/github/workflow-runs?limit=50');
        workflowRuns = wfRes.data.items || [];
      } catch (e) {
        // Ignore workflow fetch errors
      }

      // Combine and filter
      const combined = [
        ...(activityRes.data.items || []),
        ...workflowRuns
      ];

      // Sort by updated_at desc
      combined.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

      setActivityItems(combined);

      // Ensure consistency: If RAG stats say 0 but we have items, show the item count
      setStats(prev => {
        if (prev.documents === 0 && combined.length > 0) {
          return {
            ...prev,
            documents: combined.length
          };
        }
        return prev;
      });
    } catch (e) {
      console.error("Activity fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleRunIndexer = async () => {
    setIndexing(true);
    setIndexError(null);

    try {
      // Index all federated sources
      await api.post('/federated-search/index/all');

      // Refresh dashboard data after indexing
      await fetchDashboardData();
    } catch (e) {
      console.error("Indexing failed:", e);
      setIndexError(e.response?.data?.detail || e.message || "Indexing failed");
    } finally {
      setIndexing(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50/50">

      {/* --- DASHBOARD HEADER --- */}
      <div className="p-6 pb-2">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Data Graph</h2>
            <p className="text-gray-500 mt-1">Real-time status of your indexed engineering intelligence.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleRunIndexer}
              disabled={indexing}
              className={`px-4 py-2 font-medium rounded-lg transition-colors flex items-center gap-2 ${indexing
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
            >
              {indexing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {indexing ? 'Indexing...' : 'Run Indexer'}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {indexError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <span className="text-sm text-red-700">{indexError}</span>
            <button
              onClick={() => setIndexError(null)}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard
            label="Repositories Indexed"
            value={stats.repos}
            icon={Server}
            color="bg-purple-600"
          />
          <StatCard
            label="Vector Embeddings"
            value={(stats.vectors / 1000).toFixed(1) + 'k'}
            subValue={`${stats.vectors.toLocaleString()} raw`}
            icon={Database}
            color="bg-blue-600"
          />
          <StatCard
            label="Knowledge Documents"
            value={stats.documents}
            icon={FileText}
            color="bg-emerald-600"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('codebase')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'codebase'
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
          >
            <Network className="w-4 h-4" />
            Codebase Graph
          </button>
          <button
            onClick={() => setActiveTab('knowledge')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'knowledge'
              ? 'border-emerald-600 text-emerald-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
          >
            <Database className="w-4 h-4" />
            Knowledge Graph
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'activity'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
          >
            <Activity className="w-4 h-4" />
            Team Activity
          </button>
        </div>
      </div>

      {/* --- CONTENT AREA --- */}
      <div className="flex-1 overflow-auto p-6 pt-2">

        {/* CODEBASE VIEW */}
        {activeTab === 'codebase' && (
          <div className="h-full grid grid-cols-12 gap-6">
            {/* Sidebar: Repositories */}
            <div className="col-span-3 overflow-y-auto pr-2">
              <div className="flex items-center justify-between mb-4 px-1">
                <h3 className="text-sm font-semibold text-gray-900">Repositories</h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{stats.repos}</span>
              </div>
              <RepositoryList
                repos={repos}
                activeRepo={selectedRepo}
                onSelect={setSelectedRepo}
              />
            </div>

            {/* Main: Graph */}
            <div className="col-span-9 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden relative flex flex-col">
              <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                <p className="text-xs font-medium text-gray-500">Visualizing</p>
                <p className="text-sm font-bold text-gray-900">{selectedRepo || 'Local Backend'}</p>
              </div>
              <div className="flex-1 bg-slate-50 relative">
                {/* Graph Component - empty string triggers fallback to local backend RAG */}
                <CodeGraphVisualization repoKey={selectedRepo || ""} />
              </div>
            </div>
          </div>
        )}

        {/* KNOWLEDGE GRAPH VIEW */}
        {activeTab === 'knowledge' && (
          <div className="h-full flex flex-col bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" style={{ minHeight: '70vh' }}>
            {/* Data Type Selector */}
            <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-4 flex-shrink-0">
              <span className="text-sm font-medium text-gray-700">Data Type:</span>
              <div className="flex gap-2">
                {[
                  { key: 'all', label: 'All Data', icon: Database },
                  { key: 'text', label: 'Text/Docs', icon: FileText },
                  { key: 'logs', label: 'Logs', icon: FileCode },
                  { key: 'metrics', label: 'Metrics', icon: BarChart3 }
                ].map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setSelectedRagType(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${selectedRagType === key
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-700'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {/* Graph Visualization - Full height container */}
            <div className="flex-1 min-h-0">
              <DataGraphVisualization ragType={selectedRagType} />
            </div>
          </div>
        )}

        {/* TEAM ACTIVITY VIEW */}
        {activeTab === 'activity' && (
          <div className="h-full grid grid-cols-12 gap-6">
            {/* List */}
            <div className="col-span-5 flex flex-col h-full bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search activity..."
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  />
                </div>
                <button className="p-2 text-gray-400 hover:text-gray-700 bg-white border border-gray-200 rounded-lg">
                  <Filter className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <ActivityList
                  items={activityItems}
                  onItemSelect={setSelectedActivity}
                  selectedItem={selectedActivity}
                />
              </div>
            </div>

            {/* Details / Preview */}
            <div className="col-span-7 bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col justify-center items-center text-center">
              {selectedActivity ? (
                <div className="w-full h-full flex flex-col text-left">
                  <div className="mb-6 pb-6 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${selectedActivity.type === 'PullRequest' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                        {selectedActivity.type || 'Activity'}
                      </span>
                      <span className="text-sm text-gray-400">
                        ID: {selectedActivity.id}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                      {selectedActivity.name || selectedActivity.title}
                    </h2>
                  </div>
                  <div className="prose prose-sm max-w-none text-gray-600">
                    <p>{selectedActivity.content || selectedActivity.description || "No detailed content available."}</p>
                    <pre className="bg-gray-50 p-4 rounded-lg text-xs font-mono text-gray-700 overflow-auto mt-4 border border-gray-100">
                      {JSON.stringify(selectedActivity, null, 2)}
                    </pre>
                  </div>
                  <div className="mt-auto pt-6 flex justify-end">
                    <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                      Open in Source <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="max-w-md">
                  <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Activity className="w-8 h-8 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Select an item</h3>
                  <p className="text-gray-500 mt-2">Click on any activity item from the list to view its full details, metadata, and source context.</p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
